import React, { useEffect, useRef } from 'react';
import * as PIXI from 'pixi.js';
import './AudioVisualization.css';

const AudioVisualization = ({ audioRef, isPlaying }) => {
  const containerRef = useRef(null);
  const appRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const isPlayingRef = useRef(isPlaying);
  const hasConnectedRef = useRef(false); // Флаг, что audio уже был подключен
  
  // Обновляем ref при изменении isPlaying
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    let isMounted = true;
    const container = containerRef.current;

    const initPixi = async () => {
      try {
        // Ждем размеры
        await new Promise(resolve => setTimeout(resolve, 200));
        
        const rect = container.getBoundingClientRect();
        const width = rect.width || container.clientWidth || 300;
        const height = rect.height || container.clientHeight || 120;

        if (width === 0 || height === 0) {
          setTimeout(() => {
            if (isMounted && containerRef.current) {
              initPixi();
            }
          }, 300);
          return;
        }

        // Создаем PixiJS приложение
        const app = new PIXI.Application();
        
        await app.init({
          width: Math.max(width, 100),
          height: Math.max(height, 100),
          backgroundColor: 0x0a0a0a,
          antialias: true,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
        });

        if (!isMounted) {
          app.destroy(true);
          return;
        }

        if (!app.canvas) {
          return;
        }

        // Очищаем и добавляем canvas
        container.innerHTML = '';
        container.appendChild(app.canvas);
        appRef.current = app;

        // Создаем графику
        const graphics = new PIXI.Graphics();
        app.stage.addChild(graphics);

        // Запускаем анимацию
        const bars = 80;
        let frame = 0;
        
        const animate = () => {
          if (!isMounted || !graphics || !app) {
            return;
          }

          frame++;
          graphics.clear();

          // Проверяем, играет ли музыка (используем ref для актуального значения)
          const isMusicPlaying = isPlayingRef.current && audioRef?.current && !audioRef.current.paused;
          
          const barWidth = app.screen.width / bars;
          const maxBarHeight = app.screen.height * 0.85;
          const centreY = app.screen.height / 2;

          // Получаем данные только если музыка играет
          let frequencyData = null;
          let hasRealData = false;
          
          if (isMusicPlaying && analyserRef.current && dataArrayRef.current) {
            try {
              analyserRef.current.getByteFrequencyData(dataArrayRef.current);
              frequencyData = dataArrayRef.current;
              const maxValue = Math.max(...Array.from(frequencyData));
              hasRealData = maxValue > 3;
            } catch (err) {
              // Игнорируем
            }
          }

          // Если музыка не играет, просто очищаем
          if (!isMusicPlaying) {
            animationFrameRef.current = requestAnimationFrame(animate);
            return;
          }

          // Если нет реальных данных, не показываем визуализацию
          if (!hasRealData || !frequencyData) {
            animationFrameRef.current = requestAnimationFrame(animate);
            return;
          }

          // Рисуем бары только с реальными данными
          for (let i = 0; i < bars; i++) {
            let barHeight = 0;
            
            const dataIndex = Math.floor((i / bars) * frequencyData.length);
            const value = frequencyData[dataIndex] / 255;
            barHeight = value * maxBarHeight;

            if (barHeight < 1) barHeight = 1;

            const x = i * barWidth + barWidth / 2;
            const topY = centreY - barHeight / 2;
            const bottomY = centreY + barHeight / 2;

            // Цвет
            let color = 0x6a6a6a; // Светлее базовый цвет
            
            if (barHeight > 1) {
              const normalizedHeight = Math.min(1, barHeight / maxBarHeight);
              
              if (normalizedHeight < 0.3) {
                const intensity = normalizedHeight / 0.3;
                // Увеличиваем яркость зеленого
                color = (0 << 16) | (Math.floor(255 * intensity) << 8) | Math.floor(150 * intensity);
              } else if (normalizedHeight < 0.6) {
                const intensity = (normalizedHeight - 0.3) / 0.3;
                // Увеличиваем яркость желтого/оранжевого
                color = (Math.floor(255 * intensity) << 16) | (Math.floor(230 * intensity) << 8) | 0;
              } else {
                const intensity = Math.min(1, (normalizedHeight - 0.6) / 0.4);
                // Увеличиваем яркость красного
                color = (255 << 16) | (Math.floor(150 * (1 - intensity)) << 8) | 0;
              }
            }

            // Рисуем
            if (barHeight > 1) {
              graphics.lineStyle(0);
              graphics.beginFill(color, 1.0); // Увеличиваем непрозрачность для более яркого вида
              graphics.drawRect(x - barWidth / 2 + 1, topY, barWidth - 2, barHeight);
              graphics.endFill();

              if (barHeight > maxBarHeight * 0.2) {
                graphics.lineStyle(2, color, 0.8); // Увеличиваем непрозрачность линии
                graphics.moveTo(x, topY);
                graphics.lineTo(x, bottomY);
              }

              // Верхняя подсветка
              if (barHeight > maxBarHeight * 0.3) {
                const r = Math.min(255, ((color >> 16) & 0xFF) + 70); // Увеличиваем яркость подсветки
                const g = Math.min(255, ((color >> 8) & 0xFF) + 70);
                const b = Math.min(255, (color & 0xFF) + 70);
                const highlightColor = (r << 16) | (g << 8) | b;
                
                graphics.lineStyle(0);
                graphics.beginFill(highlightColor, 0.6); // Увеличиваем непрозрачность подсветки
                graphics.drawRect(x - barWidth / 2 + 1, topY, barWidth - 2, Math.min(barHeight * 0.2, 5));
                graphics.endFill();
              }
            } else {
              graphics.lineStyle(1, 0x4a4a4a, 0.5); // Светлее для неактивных баров
              graphics.moveTo(x, centreY - 1);
              graphics.lineTo(x, centreY + 1);
            }
          }

          // Центральная линия
          graphics.lineStyle(1, 0x3a3a3a, 0.7); // Светлее центральная линия
          graphics.moveTo(0, centreY);
          graphics.lineTo(app.screen.width, centreY);

          animationFrameRef.current = requestAnimationFrame(animate);
        };

        // Запускаем анимацию
        animate();

      } catch (err) {
        console.error('Error initializing PixiJS:', err);
      }
    };

    initPixi();

    return () => {
      isMounted = false;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      if (appRef.current) {
        try {
          appRef.current.destroy(true);
        } catch (err) {
          // Игнорируем
        }
        appRef.current = null;
      }
      
      // НЕ освобождаем AudioContext при размонтировании компонента
      // Это предотвратит блокировку воспроизведения
      // Ресурсы будут освобождены только при полной перезагрузке страницы
    };
  }, [audioRef]);

  // Отдельный эффект для управления AudioContext при изменении isPlaying
  useEffect(() => {
    // КРИТИЧНО: Подключаемся к audio ТОЛЬКО ОДИН РАЗ при первом запуске
    // И НИКОГДА не освобождаем ресурсы, чтобы не блокировать воспроизведение
    if (isPlaying && audioRef?.current && audioRef.current.src && !audioContextRef.current && !hasConnectedRef.current) {
      // Инициализируем только если музыка играет, контекста еще нет и мы еще не подключались
      const initAudioContext = async () => {
        try {
          if (!audioRef?.current || !audioRef.current.src) {
            return;
          }

          const audio = audioRef.current;

          // Ждем готовности аудио
          if (audio.readyState < 2) {
            await new Promise((resolve) => {
              const timeout = setTimeout(resolve, 300);
              const onCanPlay = () => {
                clearTimeout(timeout);
                audio.removeEventListener('canplay', onCanPlay);
                resolve();
              };
              audio.addEventListener('canplay', onCanPlay);
            });
          }

          // Создаем контекст
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          
          if (audioContext.state === 'suspended') {
            try {
              await audioContext.resume();
            } catch (err) {
              // Игнорируем
            }
          }

          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 512;
          analyser.smoothingTimeConstant = 0.7;

          const bufferLength = analyser.frequencyBinCount;
          const dataArray = new Uint8Array(bufferLength);
          dataArrayRef.current = dataArray;

          // Пытаемся подключиться к audio элементу
          try {
            const source = audioContext.createMediaElementSource(audio);
            source.connect(analyser);
            analyser.connect(audioContext.destination);

            analyserRef.current = analyser;
            audioContextRef.current = audioContext;
            sourceRef.current = source;
            hasConnectedRef.current = true; // Помечаем, что подключились
            
            console.log('✅ Audio connected to analyser (one-time connection)');
          } catch (connectError) {
            // Если audio уже подключен, просто не используем визуализацию с реальными данными
            console.warn('⚠️ Audio already connected:', connectError.message);
            console.log('ℹ️ Visualization will work without real audio data');
            
            // Закрываем контекст
            try {
              audioContext.close();
            } catch (err) {
              // Игнорируем
            }
            
            // Помечаем, что audio уже подключен, чтобы больше не пытаться
            hasConnectedRef.current = true;
          }
        } catch (err) {
          console.warn('Audio context error (non-critical):', err);
        }
      };

      // Используем небольшую задержку
      const timeout = setTimeout(() => {
        initAudioContext().catch(err => {
          console.warn('Audio context initialization failed:', err);
        });
      }, 200);
      
      return () => clearTimeout(timeout);
    }
    // НЕ освобождаем ресурсы при остановке - это предотвратит блокировку воспроизведения
  }, [isPlaying, audioRef]);

  return (
    <div className="audio-visualization-container">
      <div ref={containerRef} className="audio-visualization-canvas" />
    </div>
  );
};

export default AudioVisualization;
